/**
 * Mock API Facade
 * 
 * This module provides a centralized way to return test data for specific API endpoints.
 * When test mode is enabled, API calls are intercepted and test data is returned instead.
 * 
 * Benefits:
 * - Service files remain clean (no test data logic)
 * - Centralized test data configuration
 * - Easy to add new mocked endpoints
 * - Single performance check per API call
 */

import testSubscriptions from "@/assets/test-data/subscriptions.json";
import testTenants from "@/assets/test-data/tenants.json";
import testInvoices from "@/assets/test-data/invoices.json";
import testContracts from "@/assets/test-data/contracts.json";
import testProducts from "@/assets/test-data/products.json";
import testTenantUsers from "@/assets/test-data/tenant-users.json";
import type { ChargebeeSubscription } from "@/types";

// Check if test mode is enabled
// IMPORTANT: Always false in production, regardless of .env setting
const IS_TEST_MODE = 
  import.meta.env.VITE_APP_ENVIRONMENT !== "production" && 
  import.meta.env.VITE_USE_TEST_DATA === "true";

/**
 * Configuration for mocked endpoints
 * Maps API paths (with optional wildcards) to test data and scenarios
 */
interface MockEndpoint<T> {
  testData: Record<string, T>;
  scenario: string;
  delay?: number; // Optional delay in ms to simulate API latency
}

/**
 * Registry of all mocked endpoints
 * Add new endpoints here when test data is available
 */
const MOCK_REGISTRY: Record<string, MockEndpoint<unknown>> = {
  // Subscription endpoint - matches /account/{tenantId}/subscription
  "account/*/subscription": {
    testData: testSubscriptions,
    scenario: "active_monthly",
    delay: 500
  } as MockEndpoint<ChargebeeSubscription>,
  
  // Tenant list endpoint - matches /tenant
  "tenant": {
    testData: testTenants,
    scenario: "all_tenants",
    delay: 300
  },
  // Single tenant endpoint - matches /tenant/{id}
  "tenant/*": {
    testData: testTenants,
    scenario: "single_tenant",
    delay: 300
  },
  // Invoice list endpoint - matches /{tenantId}/invoice
  "*/invoice": {
    testData: testInvoices,
    scenario: "all_invoices",
    delay: 400
  },
  // Tenant users endpoint - matches /tenant/{id}/user
  "tenant/*/user": {
    testData: testTenantUsers,
    scenario: "tenant_1_users",
    delay: 300
  },
  // Contract list endpoint - matches /{tenantId}/contract
  "*/contract": {
    testData: testContracts,
    scenario: "all_contracts",
    delay: 400
  },
  // Product list endpoint - matches /{tenantId}/product
  "*/product": {
    testData: testProducts,
    scenario: "all_products",
    delay: 400
  }
};

/**
 * Checks if an API path matches a mock endpoint pattern
 * Supports wildcards (*) in patterns
 */
function matchesPattern(path: string, pattern: string): boolean {
  // Remove leading/trailing slashes for consistent matching
  const cleanPath = path.replace(/^\/|\/$/g, "");
  const cleanPattern = pattern.replace(/^\/|\/$/g, "");
  
  // Convert pattern to regex (replace * with \d+ for numeric IDs)
  const regexPattern = cleanPattern
    .replace(/\*/g, "[^/]+") // Match any characters except /
    .replace(/\//g, "\\/");  // Escape forward slashes
  
  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(cleanPath);
}

/**
 * Attempts to get mock data for the given API path
 * Returns null if no mock is configured or test mode is disabled
 */
export async function getMockData(service: string, path: string): Promise<unknown | null> {
  if (!IS_TEST_MODE) return null;
  
  // Find matching mock endpoint
  for (const [pattern, config] of Object.entries(MOCK_REGISTRY)) {
    if (matchesPattern(path, pattern)) {
      // Simulate API delay if configured
      if (config.delay) {
        await new Promise(resolve => setTimeout(resolve, config.delay));
      }
      
      return config.testData[config.scenario as string];
    }
  }
  
  return null;
}

/**
 * Updates the test scenario for a specific endpoint pattern
 * Useful for switching between different test scenarios at runtime
 * 
 * @example
 * setMockScenario("account/&#42;/subscription", "paused");
 */
export function setMockScenario<T>(pattern: string, scenario: string): void {
  const config = MOCK_REGISTRY[pattern] as MockEndpoint<T> | undefined;
  if (config && scenario in config.testData) {
    config.scenario = scenario as keyof typeof config.testData;
  } else {
    console.warn(`Mock endpoint "${pattern}" not found or invalid scenario "${scenario}"`);
  }
}

/**
 * Gets the current scenario for a mocked endpoint
 */
export function getMockScenario(pattern: string): string | null {
  const config = MOCK_REGISTRY[pattern];
  return config ? String(config.scenario) : null;
}

/**
 * Lists all available scenarios for a mocked endpoint
 */
export function listMockScenarios(pattern: string): string[] {
  const config = MOCK_REGISTRY[pattern];
  return config ? Object.keys(config.testData) : [];
}
